/**
 * Error display component for showing playback errors to users
 * Extends media-chrome patterns for proper integration with media-controller
 */
import { t } from 'media-chrome/utils/i18n.js';

/** Set on the media-controller while an error overlay is showing. */
const ERROR_STATE_ATTR = 'data-error';

/**
 * Custom element that displays player errors as a fullscreen overlay.
 * Uses slot="centered-chrome" with noautohide to ensure visibility.
 *
 * @example
 * <bunny-error-display slot="centered-chrome" noautohide></bunny-error-display>
 */
class BunnyErrorDisplay extends HTMLElement {
  /**
   * The controller we subscribed to and mirror state onto. Held rather than
   * re-resolved, because by disconnect time closest() may find nothing and we would
   * leave data-error set - which keeps the play button hidden for good.
   */
  #mediaController: Element | null = null;

  static get observedAttributes() {
    return ['visible'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.#render();
  }

  connectedCallback() {
    // Listen on media-controller for bubbled events from bunny-stream-video
    this.#mediaController = this.closest('media-controller');
    if (this.#mediaController) {
      this.#mediaController.addEventListener('playererror', this.#handleError);
    }

    // Also listen at document level for events that might not bubble through shadow DOM
    document.addEventListener('playererror', this.#handleError);

    // `visible` may have been set before we were connected, when there was no controller.
    this.#reflectErrorStateToController();
  }

  disconnectedCallback() {
    if (this.#mediaController) {
      this.#mediaController.removeEventListener(
        'playererror',
        this.#handleError,
      );
      this.#mediaController.removeAttribute(ERROR_STATE_ATTR);
      this.#mediaController = null;
    }
    document.removeEventListener('playererror', this.#handleError);
  }

  attributeChangedCallback(name: string) {
    if (name !== 'visible') return;
    this.#reflectErrorStateToController();
  }

  /**
   * Mirror our visibility onto the controller so the stylesheet can react to it.
   *
   * The player has several things that want the middle of the frame - this overlay, the
   * server-rendered status message, and the big play button - and each was deciding to
   * show itself in isolation, so they stacked up (STREAM-829). CSS cannot look at a
   * descendant's state without :has(), which is above our browser floor, so the state
   * comes up to the controller as an attribute instead.
   */
  #reflectErrorStateToController() {
    const mediaController = this.#mediaController ??
      this.closest('media-controller');
    if (!mediaController) return;

    if (this.hasAttribute('visible')) {
      mediaController.setAttribute(ERROR_STATE_ATTR, 'true');
    } else {
      mediaController.removeAttribute(ERROR_STATE_ATTR);
    }
  }

  #render() {
    if (!this.shadowRoot) return;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: none;
          position: absolute;
          inset: 0;
          justify-content: center;
          align-items: center;
          background-color: rgba(0, 0, 0, 0.8);
          color: #ccc;
          font-size: 14px;
          line-height: 1.5;
          text-align: center;
          padding: 20px;
          box-sizing: border-box;
        }

        :host([visible]) {
          display: flex;
        }

        .error-text {
          max-width: 80%;
        }
      </style>
      <div class="error-text" part="text"></div>
    `;
  }

  #handleError = (event: Event) => {
    const customEvent = event as CustomEvent<{
      type: string;
      code: string;
      message: string;
    }>;

    if (!customEvent.detail?.message) return;

    const { message } = customEvent.detail;
    // Our own keys are registered with addTranslation(), so `t` resolves them at
    // runtime, but its parameter type is Media Chrome's own fixed key union.
    const translatedMessage = t(message as Parameters<typeof t>[0]);

    const textEl = this.shadowRoot?.querySelector('.error-text');
    if (textEl) {
      textEl.textContent = translatedMessage || message;
    }

    this.setAttribute('visible', '');
  };

  hide() {
    this.removeAttribute('visible');
  }

  show(message: string) {
    const textEl = this.shadowRoot?.querySelector('.error-text');
    if (textEl) {
      textEl.textContent = message;
    }
    this.setAttribute('visible', '');
  }
}

customElements.define('bunny-error-display', BunnyErrorDisplay);

export default BunnyErrorDisplay;
